import {
  AskConfirmation,
  getConfirmationButtons,
  ConfirmationButtonType
} from '../../../webmodule-common/other/ui/modal-confirmation';
import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { customElement } from 'lit/decorators.js';
import { DataCache } from '../../../webmodule-common/cache/data-cache';
import { DataCacheGeneric } from '../../../webmodule-common/cache/generic-data-cache';
import { DataEntryOwner } from '../../../webmodule-common/other/ui/DataEntryOwner';
import { DataEntryPageControlView, ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { EventSnippet, EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import { getApiFactory } from '../../api/api-injector';
import { getConfirmationReasonFor, ReasonOption } from '../../../webmodule-common/other/ui/modal-confirmation-reason';
import { html } from 'lit';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import {
  MenuIconOption,
  PageButtonLocation,
  PageControl,
  PageControlOptions,
  PageManager
} from '../../../webmodule-common/other/ui/page-control';
import { ProjectApi } from '../../api/project-api';
import { ProjectContainer, ProjectContainerManager } from '../data/project-container';
import { ProjectDetailView } from './project-detail-view';
import { ProjectDocumentView } from './project-document-view';
import { ProjectResourceView } from './project-resource-view';
import {
  ProjectFinalStateReason,
  ProjectState,
  ViewProjectResourceReference
} from '../../api/dealer-api-interface-project';
import { QuoteCacheData } from '../../dealer-franchisee/cache-impl/cache-data';
import { SSEProject } from '../data/sse-project';
import { LangProjectView } from '../../../localization/scripts/projects/project-view-lang';
import { WMEventSource } from '../../api/event-source';
import { glossary } from '../../../localization/global';

export interface ProjectViewOptions {
  projectContainerManager: ProjectContainerManager;
  quoteCache: DataCache<QuoteCacheData>;
  purchaseOrderCache: DataCacheGeneric;
  title: EventSnippet;
}

// Project view encloses the entire project edit and view process
// contains detail view
// resources view
// shipping view.
@customElement('wm-projectdataentryview')
export class ProjectDataEntryView extends DataEntryPageControlView {
  private readonly discardChangesText = LangProjectView.discardChangesText();
  projectApi: ProjectApi = getApiFactory().project();
  quoteCache: DataCache<QuoteCacheData>;
  purchaseOrderCache: DataCacheGeneric;
  protected readonly projectContainerManager: ProjectContainerManager;
  protected eventTitle: EventSnippet;
  protected readonly detailView: ProjectDetailView;
  private readonly resourceView: ProjectResourceView;
  private readonly documentsView: ProjectDocumentView;

  constructor(options: ProjectViewOptions, owner: DataEntryOwner) {
    super(owner);
    this.eventTitle = options.title;
    this.quoteCache = options.quoteCache;
    this.purchaseOrderCache = options.purchaseOrderCache;

    this.projectContainerManager = options.projectContainerManager;

    this.detailView = this.projectDetailViewFactory(options);
    this.resourceView = this.projectResourceViewFactory();
    this.documentsView = this.projectDocumentsViewFactory();

    this.projectContainerManager.afterSave.push(async () => {
      this.detailView.requestUpdate();

      // await this.resourceView.loadOrRefresh();
      // this.resourceView.render();

      this.requestUpdate();
      this.dispatchUiChanged();
    });
  }

  get projectContainer(): ProjectContainer {
    return this.projectContainerManager.container;
  }

  private _eventProject = async (_data: unknown, _eventName: string) => {
    const data = _data as SSEProject;
    if (
      data.id === this.projectContainer.projectId &&
      data.recordVersion !== this.projectContainerManager.project?.recordVersion
    ) {
      if (this.projectContainerManager.isReadonly()) this.doResourceChanged();
    }
  };

  public async afterConstruction() {
    WMEventSource.getInstance().addEventListener(WMEventSource.project, this._eventProject);
    await this.projectContainerManager.needsProject();
    await constructAsync(this.detailView);
    await super.afterConstruction();
    this.buildProjectActionMenu();
  }

  async openResourceItem(resourceItem: ViewProjectResourceReference): Promise<void> {
    alert(`Open Resource ${resourceItem.typeOf.toString()}`);
  }

  public async canLoseFocus(): Promise<boolean> {
    return true;
  }

  public override isDataReadonly(): boolean {
    return this.projectContainerManager.isReadonly();
  }

  public override async prepareForSave(): Promise<void> {
    if (this.isDataReadonly()) return;

    await this.detailView.prepareForSave();
  }

  public override internalDataChanged(): boolean {
    return this.projectContainerManager.changed();
  }

  public override getDataDictionaryName(): string {
    return glossary('project');
  }

  public getTitle(): Snippet {
    return html`${this.eventTitle(this.projectContainer.project)}`;
  }

  protected override getValidationErrors() {
    const errors = [...this.detailView.getValidationErrors()];
    return errors;
  }

  protected createPageControl(): PageControl {
    // build static pages for each of the configured table settings
    const getInitialPageManagers = (): PageManager[] => {
      const pages: PageManager[] = [];
      pages.push(this.createDetailPage());
      pages.push(this.createResourcePage());
      pages.push(this.createDocumentPage());
      return pages;
    };
    const options: PageControlOptions = {
      defaultTabIndex: 0,
      menuIcons: undefined,
      pageInitializer: () => getInitialPageManagers()
    };
    const pc = new PageControl(options);
    pc.pageButtonLocation = PageButtonLocation.right;
    return pc;
  }

  protected createResourcePage(): PageManager {
    return {
      caption: () => LangProjectView.documentsTabTitle(),
      canClose: () => Promise.resolve(false),
      canLeave: async () => await this.allowPageSwitch(),
      hasDelete: () => false,
      onEnter: async () => {
        await this.resourceView.invalidate();
      },
      content: () => {
        return this.resourceView.ui;
      },
      buttonMenu: () => {
        return this.resourceView.buttonMenu();
      },
      data: this.resourceView,
      pageFragment: 'resources'
    };
  }

  protected createDocumentPage(): PageManager {
    return {
      caption: () => LangProjectView.reportsTabTitle(),
      canClose: () => Promise.resolve(false),
      canLeave: async () => await this.allowPageSwitch(),
      hasDelete: () => false,
      onEnter: async () => {
        await this.documentsView.invalidate();
      },
      content: () => {
        return this.documentsView.ui;
      },
      buttonMenu: () => {
        return this.documentsView.buttonMenu();
      },
      data: this.documentsView,
      pageFragment: 'documents'
    };
  }

  protected projectResourceViewFactory() {
    return new ProjectResourceView({
      projectManager: this.projectContainerManager,
      quoteCache: this.quoteCache,
      purchaseOrderCache: this.purchaseOrderCache,
      canClose: async () => {
        if (await this.canClose()) {
          await this.tryClose();
          return true;
        }

        return false;
      }
    });
  }

  protected projectDocumentsViewFactory() {
    return new ProjectDocumentView({
      projectManager: this.projectContainerManager,
      canClose: async () => {
        if (await this.canClose()) {
          await this.tryClose();
          return true;
        }

        return false;
      }
    });
  }

  protected projectDetailViewFactory(_options: ProjectViewOptions) {
    return new ProjectDetailView({
      projectManager: this.projectContainerManager
    });
  }

  protected bodyTemplate(): EventTemplate {
    this.buildProjectActionMenu();
    return super.bodyTemplate();
  }

  protected async setProjectState(projectState: ProjectState): Promise<boolean> {
    try {
      if (projectState == ProjectState.Completed) {
        if (
          await AskConfirmation(
            LangProjectView.completeProjectConfirmation(),
            getConfirmationButtons()[ConfirmationButtonType.yesNo]
          )
        ) {
          return await this.internalSetProjectState(projectState);
        }
      } else if (projectState == ProjectState.Cancelled) {
        const cancelProjectText = LangProjectView.cancelProjectButton();
        const cancelProjectMessage = LangProjectView.cancelProjectMessage();
        const cancelProjectReasonLabel = LangProjectView.cancelProjectReasonLabel();
        const hasReason = await this.tryGetUserProvidedStateChangeReason(
          cancelProjectText,
          cancelProjectMessage,
          cancelProjectReasonLabel,
          cancelProjectText
        );
        if (projectState === ProjectState.Cancelled && hasReason) {
          return await this.internalSetProjectState(projectState);
        }
      }
      return false;
    } finally {
      this.requestUpdateAndPropagate();
    }
  }

  protected async internalSetProjectState(projectState: ProjectState): Promise<boolean> {
    this.projectContainerManager.project.state = projectState;
    const saveResult = await this.internalSaveData();
    this.buildProjectActionMenu();
    return saveResult;
  }

  protected async internalCreateQuote(): Promise<boolean> {
    // implemented in subclass
    return false;
  }

  protected async canCreateQuote(): Promise<boolean> {
    // implemented in subclass
    return false;
  }

  /**
   * inherited
   * @returns
   */
  protected override async internalSaveData(): Promise<boolean> {
    //this will populate the managed quote with data from the UI
    return await this.projectContainerManager.saveProject(isAutoSaving());
  }

  protected getProjectCancellationReasons(): ReasonOption[] {
    return [
      {
        id: ProjectFinalStateReason.Deadline,
        reason: LangProjectView.finalStateReason.deadline()
      },
      {
        id: ProjectFinalStateReason.CompetitorWin,
        reason: LangProjectView.finalStateReason.competitorWin()
      },
      {
        id: ProjectFinalStateReason.UnresponsiveClient,
        reason: LangProjectView.finalStateReason.unresponsiveClient()
      },
      {
        id: ProjectFinalStateReason.IndecisiveClient,
        reason: LangProjectView.finalStateReason.indecisiveClient()
      },
      {
        id: ProjectFinalStateReason.UnhappyClient,
        reason: LangProjectView.finalStateReason.unhappyClient()
      },
      {
        id: ProjectFinalStateReason.DifficultClient,
        reason: LangProjectView.finalStateReason.difficultClient()
      },
      {
        id: ProjectFinalStateReason.Superseded,
        reason: LangProjectView.finalStateReason.superseded()
      },
      {
        id: ProjectFinalStateReason.Other,
        reason: LangProjectView.finalStateReason.other(),
        requiresComment: true
      }
    ];
  }

  protected async tryGetUserProvidedStateChangeReason(
    modalTitle: string,
    modalMessage: string,
    selectReasonLabel: string,
    confirmButtonText: string
  ): Promise<boolean> {
    const stateReasons = this.getProjectCancellationReasons();
    if (stateReasons) {
      const reasonResult = await getConfirmationReasonFor(
        stateReasons,
        modalTitle,
        modalMessage,
        selectReasonLabel,
        confirmButtonText
      );
      if (reasonResult.result && reasonResult.reason) {
        const selectedReason = reasonResult.reason;
        this.projectContainerManager.stateChangeReason = {
          comment: reasonResult.comment ?? '',
          finalStateReason: selectedReason.id
        };
        return true;
      }
      return false;
    }
    return true;
  }

  private buildProjectActionMenu() {
    const menuIcons: MenuIconOption[] = [];
    if (!this._pageControl) return;

    const cancel = {
      caption: () => LangProjectView.cancelProjectButton(),
      event: async () => {
        return await this.setProjectState(ProjectState.Cancelled);
      },
      classList: 'btn btn-dark'
    };
    const complete = {
      caption: () => LangProjectView.completeProjectButton(),
      event: async () => {
        return await this.setProjectState(ProjectState.Completed);
      }
    };

    const save = {
      event: async () => {
        if (this.isDataReadonly()) return false;

        return this.internalSaveData();
      },
      caption: () => LangProjectView.saveButton()
    };

    const status: MenuIconOption = {
      caption: () => LangProjectView.statusButton(),
      childEvents: []
    };

    const addQuote = {
      event: async () => {
        if (this.isDataReadonly()) return false;

        await this.prepareForSave();

        if (await this.canCreateQuote()) return await this.internalCreateQuote();

        return false;
      },
      caption: () => LangProjectView.addQuoteButton()
    };

    if (!isAutoSaving()) menuIcons.push(save);

    if (!this.projectContainerManager.isReadonly()) {
      status.childEvents = [complete, cancel];
      menuIcons.push(status);
      menuIcons.push(addQuote);
    }

    this.pageControl.setMenuIcons(menuIcons);
  }

  private createDetailPage(): PageManager {
    return {
      caption: () => LangProjectView.detailsTabTitle(),
      canClose: () => Promise.resolve(false),
      canLeave: async () => await this.allowPageSwitch(),
      hasDelete: () => false,
      onEnter: async () => {
        await this.detailView.loadOrRefresh();
      },
      content: () => {
        return this.detailView.ui;
      },
      buttonMenu: () => {
        if (this.isLockedFromUse || this.isDataReadonly()) return html``;
        return html` <webmodule-button size="small" variant="default" @click=${() => this.abandonAndClose()}>
          ${this.discardChangesText}
        </webmodule-button>`;
      },
      data: this.detailView,
      pageFragment: 'details',
      pageButtonLocation: PageButtonLocation.left
    };
  }

  private get isLockedFromUse(): boolean {
    return this.projectContainerManager.isLockedFromUse;
  }

  protected doResourceChanged() {
    this.ui.dispatchEvent(
      new CustomEvent('resource-changed', {
        bubbles: true,
        composed: true,
        detail: {}
      })
    );
  }

  override async dispose(): Promise<void> {
    WMEventSource.getInstance().removeEventListener(WMEventSource.project, this._eventProject);

    await super.dispose();
  }
}

@customElement('wm-projectview')
export class ProjectView extends ModalViewBase {
  view: ProjectDataEntryView | null = null;
  options: ProjectViewOptions;

  constructor(options: ProjectViewOptions) {
    super();
    this.options = options;
  }

  /**
   * inherited
   * @returns
   */
  override async canClose(): Promise<boolean> {
    return (await this.view?.canClose()) ?? true;
  }

  override async afterConstruction(): Promise<void> {
    this.view = await constructAsync(this.createView());
  }

  /**
   * inherited
   * @returns
   */
  protected override getTitle(): Snippet {
    return this.view?.getTitle() ?? '';
  }

  /**
   * inherited
   * @returns
   */
  protected get modalSize() {
    return 'modal-fullscreen';
  }

  protected createView(): ProjectDataEntryView {
    return new ProjectDataEntryView(this.options, this);
  }

  protected bodyTemplate(): EventTemplate {
    return html`${this.view?.ui}`;
  }
}
